import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  QuestionMarkCircleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  XMarkIcon,
  InboxStackIcon,
} from '@heroicons/react/24/solid';
import FeedbackModal from './FeedbackModal';
import MyFeedbackPanel from './MyFeedbackPanel';
import { useFiles } from '../contexts/FilesContext';
import { InvoiceData } from '../types/invoice';
import { api } from '../services/api';

const LAST_SEEN_KEY = 'keyscan.myfeedback.lastSeenAt';

const FloatingFeedbackButton: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [myFeedbackOpen, setMyFeedbackOpen] = useState(false);
  const [hasUnreadReply, setHasUnreadReply] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const { files, invoices, selectedFileId } = useFiles();

  const readLastSeen = (): number => {
    try {
      const raw = localStorage.getItem(LAST_SEEN_KEY);
      return raw ? Number(raw) || 0 : 0;
    } catch {
      return 0;
    }
  };

  const checkForNewReply = useCallback(async () => {
    try {
      const res = await api.get('/feedback/mine/latest-reply');
      const iso: string | null = res.data?.latest_solved_at || null;
      if (!iso) {
        setHasUnreadReply(false);
        return;
      }
      const replyAt = new Date(iso).getTime();
      setHasUnreadReply(replyAt > readLastSeen());
    } catch {
      // Auth failures, network hiccups: leave the dot off so we don't
      // pester a user who isn't even logged in.
      setHasUnreadReply(false);
    }
  }, []);

  useEffect(() => {
    checkForNewReply();
    const onFocus = () => checkForNewReply();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [checkForNewReply]);

  // Close menu when clicking outside
  useEffect(() => {
    if (!menuOpen) return;
    const onClickAway = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickAway);
    return () => document.removeEventListener('mousedown', onClickAway);
  }, [menuOpen]);

  const selectedFile = selectedFileId
    ? invoices.find((f) => f.id === selectedFileId) ||
      files.find((f) => f.id === selectedFileId)
    : undefined;
  const invoiceData: InvoiceData | undefined =
    selectedFile?.editedData || selectedFile?.result?.data;
  const invoiceId = selectedFile?.invoiceId;
  const filename = selectedFile?.file?.name;

  const openUserGuide = () => {
    // Points at the Keystone-hosted user guide so it can be updated
    // without redeploying KeyScan.
    window.open(
      'https://keyinfosys.com/ClientCare/documents/user_manuals/Financials/KeyScan%20AP/KeyScan%20AP%20User%20Guide.pdf',
      '_blank',
      'noopener,noreferrer',
    );
    setMenuOpen(false);
  };

  const openFeedback = () => {
    setFeedbackOpen(true);
    setMenuOpen(false);
  };

  const openMyFeedback = () => {
    setMyFeedbackOpen(true);
    setMenuOpen(false);
    try {
      localStorage.setItem(LAST_SEEN_KEY, String(Date.now()));
    } catch {
      // localStorage unavailable (private mode etc); dot will just
      // keep showing until state clears, which is acceptable.
    }
    setHasUnreadReply(false);
  };

  return (
    <>
      <div ref={menuRef} className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-2">
        {menuOpen && (
          <div className="flex flex-col gap-2 mb-1">
            <button
              onClick={openUserGuide}
              className="group flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-all"
              title="Open the KeyScan AP user guide (PDF)"
            >
              <BookOpenIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <span className="text-sm font-medium">User Guide</span>
            </button>
            <button
              onClick={openFeedback}
              className="group flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-all"
              title={invoiceId ? 'Send feedback about this invoice' : 'Send general feedback'}
            >
              <ChatBubbleLeftRightIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <span className="text-sm font-medium">
                {invoiceId ? 'Feedback on This Invoice' : 'Send Feedback'}
              </span>
            </button>
            <button
              onClick={openMyFeedback}
              className="group relative flex items-center gap-2 pl-3 pr-4 py-2 rounded-full bg-white dark:bg-gray-800 text-gray-800 dark:text-white shadow-lg hover:shadow-xl border border-gray-200 dark:border-gray-700 transition-all"
              title={hasUnreadReply ? 'New reply on your feedback' : "See the status of feedback you've submitted"}
            >
              <InboxStackIcon className="h-5 w-5 text-brand-600 dark:text-brand-400" />
              <span className="text-sm font-medium">My Feedback</span>
              {hasUnreadReply && (
                <span className="ml-1 inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  <span className="h-1.5 w-1.5 rounded-full bg-red-500 animate-pulse" />
                  New reply
                </span>
              )}
            </button>
          </div>
        )}

        <button
          onClick={() => setMenuOpen((v) => !v)}
          className="group relative h-14 w-14 rounded-full bg-brand-600 text-white shadow-lg hover:bg-brand-700 hover:shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center justify-center focus:outline-none focus:ring-4 focus:ring-brand-300 dark:focus:ring-brand-700"
          title={menuOpen ? 'Close menu' : hasUnreadReply ? 'New reply on your feedback' : 'Feedback'}
          aria-label={menuOpen ? 'Close menu' : 'Open feedback menu'}
          aria-expanded={menuOpen}
        >
          {menuOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <QuestionMarkCircleIcon className="h-6 w-6" />
          )}
          {!menuOpen && hasUnreadReply && (
            <span
              aria-label="New reply on your feedback"
              className="absolute top-1 right-1 h-3.5 w-3.5 rounded-full bg-red-500 border-2 border-white dark:border-gray-900 animate-pulse"
            />
          )}
        </button>
      </div>

      {feedbackOpen && (
        <FeedbackModal
          onClose={() => setFeedbackOpen(false)}
          invoiceContext={
            invoiceId
              ? {
                  invoiceId,
                  filename: filename || null,
                  extractedDataSnapshot: invoiceData || null,
                }
              : null
          }
        />
      )}

      {myFeedbackOpen && (
        <MyFeedbackPanel onClose={() => setMyFeedbackOpen(false)} />
      )}
    </>
  );
};

export default FloatingFeedbackButton;
